import React, { useState, createContext, useContext } from 'react';
// ============================================================================
// TYPES
// ============================================================================
interface Proof {
  filename: string;
  uploadedAt: Date;
  status: 'pending' | 'approved' | 'rejected';
}
interface Action {
  id: string;
  title: string;
  emoji: string;
  scheduledAt?: string;
  points: number;
  type: 'gold' | 'diamond';
  status: 'planned' | 'completed' | 'pending_approval';
  proof?: Proof;
  linkedChallengeId?: string;
}
interface Challenge {
  id: string;
  title: string;
  emoji: string;
  description: string;
  duration: string;
  points: number;
  type: 'gold' | 'diamond';
  status: 'available' | 'active' | 'completed';
  progress: number;
  total: number;
  linkedActionId?: string;
}
interface ClaimedReward {
  id: string;
  rewardId: string;
  name: string;
  type: 'voucher' | 'star_reward';
  qrValue: string;
  couponCode: string;
  claimedAt: Date;
  expiresAt: Date;
  value?: number;
  logo?: string;
  description?: string;
}
interface Wallet {
  diamonds: number;
  stars: number;
}
interface ToastData {
  message: string;
  variant: 'success' | 'info' | 'error';
}
interface AppState {
  wallet: Wallet;
  actions: Action[];
  challenges: Challenge[];
  claimedRewards: ClaimedReward[];
  toast: ToastData | null;
}
interface AppContextType extends AppState {
  uploadProof: (actionId: string, file: File) => void;
  acceptChallenge: (challengeId: string) => void;
  claimReward: (rewardId: string) => string;
  showToast: (toast: ToastData) => void;
  hideToast: () => void;
}
// ============================================================================
// INITIAL MOCK DATA
// ============================================================================
const INITIAL_ACTIONS: Action[] = [{
  id: 'action-1',
  title: 'Andare in bici al lavoro',
  emoji: '🚲',
  scheduledAt: 'Oggi, 8:00',
  points: 50,
  type: 'diamond',
  status: 'planned'
}, {
  id: 'action-2',
  title: 'Piantare alberi',
  emoji: '🌳',
  scheduledAt: 'Oggi, 15:00',
  points: 80,
  type: 'diamond',
  status: 'planned'
}, {
  id: 'action-3',
  title: 'Riciclare la plastica',
  emoji: '♻️',
  scheduledAt: 'Oggi, 18:00',
  points: 10,
  type: 'gold',
  status: 'planned'
}];
const INITIAL_CHALLENGES: Challenge[] = [{
  id: 'challenge-1',
  title: 'Settimana in Bici',
  emoji: '🚲',
  description: 'Vai al lavoro in bici per 5 giorni questa settimana',
  duration: '7 giorni',
  points: 250,
  type: 'diamond',
  status: 'active',
  progress: 2,
  total: 5
}, {
  id: 'challenge-2',
  title: 'Carpooling Champion',
  emoji: '🚙',
  description: "Condividi l'auto almeno 3 volte questa settimana",
  duration: '7 giorni',
  points: 150,
  type: 'diamond',
  status: 'active',
  progress: 1,
  total: 3
}, {
  id: 'challenge-3',
  title: 'Zero Plastica',
  emoji: '♻️',
  description: 'Non usare plastica monouso per 7 giorni consecutivi',
  duration: '7 giorni',
  points: 200,
  type: 'diamond',
  status: 'available',
  progress: 0,
  total: 7
}, {
  id: 'challenge-4',
  title: 'Camminatore Urbano',
  emoji: '🚶',
  description: 'Cammina almeno 10.000 passi al giorno per una settimana',
  duration: '7 giorni',
  points: 180,
  type: 'gold',
  status: 'available',
  progress: 0,
  total: 7
}, {
  id: 'challenge-5',
  title: 'Settimana Vegetariana',
  emoji: '🥗',
  description: 'Mangia solo pasti vegetariani per 7 giorni',
  duration: '7 giorni',
  points: 220,
  type: 'diamond',
  status: 'available',
  progress: 0,
  total: 7
}, {
  id: 'challenge-6',
  title: 'Risparmio Energetico',
  emoji: '💡',
  description: 'Riduci il consumo energetico del 20% questa settimana',
  duration: '7 giorni',
  points: 160,
  type: 'gold',
  status: 'available',
  progress: 0,
  total: 1
}];
// ============================================================================
// CONTEXT
// ============================================================================
const AppContext = createContext<AppContextType | undefined>(undefined);
export function AppProvider({
  children
}: {
  children: ReactNode;
}) {
  const [wallet, setWallet] = useState<Wallet>({
    diamonds: 150,
    stars: 400
  });
  const [actions, setActions] = useState<Action[]>(INITIAL_ACTIONS);
  const [challenges, setChallenges] = useState<Challenge[]>(INITIAL_CHALLENGES);
  const [claimedRewards, setClaimedRewards] = useState<ClaimedReward[]>([]);
  const [toast, setToast] = useState<ToastData | null>(null);
  // ============================================================================
  // METHODS
  // ============================================================================
  const uploadProof = (actionId: string, file: File) => {
    setActions(prev => prev.map(action => action.id === actionId ? {
      ...action,
      status: 'pending_approval' as const,
      proof: {
        filename: file.name,
        uploadedAt: new Date(),
        status: 'pending' as const
      }
    } : action));
  };
  const acceptChallenge = (challengeId: string) => {
    // Find the challenge
    const challenge = challenges.find(c => c.id === challengeId);
    if (!challenge) return;
    // Move challenge to active
    setChallenges(prev => prev.map(c => c.id === challengeId ? {
      ...c,
      status: 'active' as const
    } : c));
    // Create linked action
    const newActionId = `action-challenge-${challengeId}-${Date.now()}`;
    const newAction: Action = {
      id: newActionId,
      title: challenge.title,
      emoji: challenge.emoji,
      scheduledAt: 'Oggi',
      points: challenge.points,
      type: challenge.type,
      status: 'planned',
      linkedChallengeId: challengeId
    };
    setActions(prev => [...prev, newAction]);
    // Update challenge with linked action ID
    setChallenges(prev => prev.map(c => c.id === challengeId ? {
      ...c,
      linkedActionId: newActionId
    } : c));
  };
  const claimReward = (rewardId: string): string => {
    // Mock reward data - in real app would come from rewards catalog
    const mockRewardData = {
      'naturasi-10': {
        name: 'NaturaSì',
        cost: 100,
        value: 10,
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2d/Naturas%C3%AC_Logo.svg/1200px-Naturas%C3%AC_Logo.svg.png',
        description: 'Buono da €10 per spesa bio su NaturaSì'
      },
      'viviverde-coop-15': {
        name: 'ViviVerde Coop',
        cost: 150,
        value: 15,
        logo: 'https://static.tecnichenuove.it/gdoweek/2014/10/vivi_verde_coop_logo_free.jpg',
        description: 'Buono da €15 su selezione ViviVerde Coop'
      },
      'lasaponaria-20': {
        name: 'La Saponaria',
        cost: 200,
        value: 20,
        logo: 'https://blog.lasaponaria.it/hubfs/LaSaponaria-5.jpg',
        description: 'Buono da €20 per cosmetici bio La Saponaria'
      }
    };
    const rewardInfo = mockRewardData[rewardId as keyof typeof mockRewardData];
    if (!rewardInfo) return '';
    // Deduct diamonds
    setWallet(prev => ({
      ...prev,
      diamonds: prev.diamonds - rewardInfo.cost
    }));
    // Create claimed reward
    const claimedRewardId = `claimed-${rewardId}-${Date.now()}`;
    const claimedReward: ClaimedReward = {
      id: claimedRewardId,
      rewardId,
      name: rewardInfo.name,
      type: 'voucher',
      qrValue: `GREENBACK-${rewardId.toUpperCase()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      couponCode: `GB${rewardInfo.value}-${Math.random().toString(36).substring(2, 6).toUpperCase()}-2024`,
      claimedAt: new Date(),
      expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
      value: rewardInfo.value,
      logo: rewardInfo.logo,
      description: rewardInfo.description
    };
    setClaimedRewards(prev => [...prev, claimedReward]);
    return claimedRewardId;
  };
  const showToast = (toastData: ToastData) => {
    setToast(toastData);
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };
  const hideToast = () => {
    setToast(null);
  };
  const value: AppContextType = {
    wallet,
    actions,
    challenges,
    claimedRewards,
    toast,
    uploadProof,
    acceptChallenge,
    claimReward,
    showToast,
    hideToast
  };
  return <AppContext.Provider value={value} data-id="element-95">{children}</AppContext.Provider>;
}
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}